// Generic analytics dashboard — parameterized by the module's DashboardConfig
// results (built by src/lib/analytics/dashboard.ts). Renders per-metric cards
// (period value + period-over-period trend + chart + optional projection) and a
// streak card, with loading / empty / error states, in the VYV dark/glass/cyan
// look. A second module inherits this by declaring a config + a rows→samples
// extractor — no new UI.
//
// GUARDRAIL: this component is DESCRIPTIVE only — values, deltas, trend arrows,
// a neutral projection readout. It renders no advice/recommendation copy, so it
// is safe for guardrail-sensitive modules (Fitness = no prescription). Any
// interpretation stays with the Mirror (LLM), never the UI.

import { TrendingUp, TrendingDown, Minus, Flame } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import type { Granularity } from "@/lib/analytics/engine";
import type { MetricResult } from "@/lib/analytics/dashboard";
import { AnalyticsChart } from "./AnalyticsChart";

const CYAN = "hsl(187 72% 60%)";
const MUTED = "hsl(222 10% 60%)";

const localeFor = (lang: Lang): string =>
  localeTagFor(lang);

const cardStyle = { background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.15)" };

function defaultFormat(v: number): string {
  return v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, "") + "k" : String(Math.round(v));
}

export interface AnalyticsDashboardProps {
  results: MetricResult[];
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  loading?: boolean;
  error?: string | null;
  /** i18n key for the empty state (no data at all). */
  emptyKey?: string;
}

const GRANS: Granularity[] = ["day", "week", "month"];

export function AnalyticsDashboard({
  results,
  granularity,
  onGranularityChange,
  loading,
  error,
  emptyKey = "analytics.empty",
}: AnalyticsDashboardProps) {
  const { t, lang } = useLanguage();
  const locale = localeFor(lang);

  const granLabel = (g: Granularity) => t("analytics.gran." + g);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-7 h-7 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <p className="text-[12px] text-center py-8" style={{ color: "hsl(0 72% 62%)" }}>{t("analytics.error")}</p>;
  }

  const hasAnyData = results.some((r) => r.buckets.length > 0);
  const streakResult = results.find((r) => r.streak)?.streak ?? null;

  return (
    <div>
      {/* Granularity toggle */}
      <div className="flex gap-2 mb-4" role="tablist" aria-label={t("analytics.gran.label")}>
        {GRANS.map((g) => (
          <button
            key={g}
            role="tab"
            aria-selected={granularity === g}
            onClick={() => onGranularityChange(g)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
            style={
              granularity === g
                ? { background: "hsl(187 72% 50% / 0.18)", color: CYAN, border: "1px solid hsl(187 72% 50% / 0.4)" }
                : { background: "hsl(222 18% 12%)", color: MUTED, border: "1px solid hsl(222 18% 20%)" }
            }
          >
            {granLabel(g)}
          </button>
        ))}
      </div>

      {!hasAnyData ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{t(emptyKey)}</p>
      ) : (
        <div className="space-y-3">
          {/* Streak card (only when a metric declares streak:true) */}
          {streakResult && (
            <div className="rounded-2xl p-4 flex items-center justify-between" style={cardStyle}>
              <div className="flex items-center gap-2.5">
                <Flame size={18} style={{ color: "hsl(35 85% 55%)" }} />
                <div>
                  <p className="font-display text-[22px] font-bold leading-none text-foreground">{streakResult.current}</p>
                  <p className="text-[10px] font-display tracking-wide" style={{ color: MUTED }}>{t("analytics.streak.current")}</p>
                </div>
              </div>
              <p className="text-[11px] font-display" style={{ color: MUTED }}>
                {t("analytics.streak.longest")}: <b className="text-foreground">{streakResult.longest}</b>
              </p>
            </div>
          )}

          {results.map((r) => (
            <MetricCard key={r.config.key} result={r} granularity={granularity} locale={locale} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({
  result,
  granularity,
  locale,
  t,
}: {
  result: MetricResult;
  granularity: Granularity;
  locale: string;
  t: (k: string) => string;
}) {
  const { config, buckets, trend, projection } = result;
  const fmt = config.format ?? defaultFormat;
  const unit = config.unitKey ? t(config.unitKey) : "";
  if (buckets.length === 0) return null;

  const TrendIcon = trend.direction === "up" ? TrendingUp : trend.direction === "down" ? TrendingDown : Minus;
  const trendColor = trend.direction === "flat" ? MUTED : CYAN;
  const pctLabel = trend.pct === null ? "—" : (trend.pct > 0 ? "+" : "") + Math.round(trend.pct) + "%";

  return (
    <div className="rounded-2xl p-4" style={cardStyle}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
            {t(config.labelKey)}
          </p>
          <p className="font-display text-[24px] font-bold leading-none text-foreground">
            {trend.currentSamples === 0 ? "—" : fmt(trend.current)}{unit && trend.currentSamples !== 0 && <span className="text-[13px] font-normal ml-0.5" style={{ color: MUTED }}>{unit}</span>}
          </p>
          <p className="text-[10px] mt-1" style={{ color: MUTED }}>{t("analytics.thisPeriod")}</p>
        </div>
        {/* Period-over-period trend — descriptive ▲/▼ only */}
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: "hsl(222 18% 12%)" }}>
          <TrendIcon size={13} style={{ color: trendColor }} />
          <span className="text-[12px] font-display font-bold" style={{ color: trendColor }}>{pctLabel}</span>
        </div>
      </div>

      <AnalyticsChart
        buckets={buckets}
        color={config.color}
        granularity={granularity}
        type={config.chart ?? "line"}
        locale={locale}
        format={fmt}
      />

      {/* Projection — neutral, descriptive readout (no advice) */}
      {projection && projection.confidence !== "low" && (
        <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid hsl(222 18% 16%)" }}>
          <span className="text-[11px] font-display" style={{ color: MUTED }}>
            {t("analytics.projection.next")}: <b className="text-foreground">{fmt(projection.nextValue)}{unit}</b>
          </span>
          <span
            className="text-[9px] font-display px-2 py-0.5 rounded-full"
            style={{ background: "hsl(187 72% 50% / 0.12)", color: CYAN }}
          >
            {t("analytics.confidence." + projection.confidence)}
          </span>
        </div>
      )}
    </div>
  );
}
